import { Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';

import { LoanApplicationOffersService } from '@shared/services/loan-application-offers/loan-application-offers.service';
import { LoanApplicationResponse, SelectedLoanAppOffersData, LoanApplicationsStates } from '@shared/models';
import { SessionService } from '@shared/services/session';
import { Utils } from '@shared/fork-common-controls/utils.service';
import { PLApplicationProgressService } from '@personal-loans/shared';
import { FeatureToggleService } from '../services/feature-toggle/feature-toggle.service';
import LoanApplicationStateEnum = LoanApplicationResponse.LoanApplicationStateEnum;
const { PersonalStateEnum } = LoanApplicationsStates;

@Component({
  selector: 'ld-loan-summary',
  templateUrl: 'loan-summary.component.html',
  styleUrls: [ 'loan-summary.component.css' ],
  providers: [ LoanApplicationOffersService ]
})
export class LoanSummaryComponent implements OnInit {

  @Input()
  public showUploadDocumentsHelpText: boolean = false;

  public isLoading: boolean = false;

  public loanDetails: SelectedLoanAppOffersData = {};

  public LoanApplicationStateMap = new Map<number, string>()
    .set(LoanApplicationStateEnum.Start, 'Start')
    .set(LoanApplicationStateEnum.Registering, 'Registering')
    .set(LoanApplicationStateEnum.LeadQuoting, 'Lead Quoting')
    .set(LoanApplicationStateEnum.Quoting, 'Quoting')
    .set(LoanApplicationStateEnum.Applying, 'Applying')
    .set(LoanApplicationStateEnum.Reviewing, 'Reviewing')
    .set(LoanApplicationStateEnum.ReviewSuspended, 'Review Suspended')
    .set(LoanApplicationStateEnum.FraudAnalyzing, 'Fraud Analyzing')
    .set(LoanApplicationStateEnum.Declined, 'Declined')
    .set(LoanApplicationStateEnum.Accepting, 'Accepting')
    .set(LoanApplicationStateEnum.AcceptingCounter, 'Accepting')
    .set(LoanApplicationStateEnum.Expired, 'Expired')
    .set(LoanApplicationStateEnum.Finalizing, 'Finalizing')
    .set(LoanApplicationStateEnum.Withdrawn, 'Withdrawn')
    .set(LoanApplicationStateEnum.Funding, 'Funding')
    .set(LoanApplicationStateEnum.Funded, 'Funded')
    .set(LoanApplicationStateEnum.PaidOff, 'PaidOff');


  public get loanNumber(): number {
    return this.loanDetails.loanApplication.loanNumber;
  }

  public get loanApplicationState(): LoanApplicationStateEnum {
    if (this.sessionService.personalLoanSession.data.loanSubmitted) {
      return LoanApplicationStateEnum.Reviewing;
    }

    return this.loanDetails.loanApplication.loanApplicationState;
  }

  public get loanApplicationStateName(): string {
    return this.LoanApplicationStateMap.has(this.loanApplicationState) ?
      this.LoanApplicationStateMap.get(this.loanApplicationState) : (console.error('LoanApplicationStateEnum Not found'), '');
  }

  public get loanAmount(): number {
    return this.loanDetails.selectedOffers[0].loanAmount;
  }

  public get termMonths(): number {
    return this.loanDetails.selectedOffers[0].termMonths;
  }

  public get monthlyPayment(): number {
    return this.loanDetails.selectedOffers[0].monthlyPayment;
  }

  public get interestRate(): string {
    return this.loanDetails.selectedOffers[0].interestRate.toFixed(2) + '%';
  }

  public get apr(): string {
    return this.loanDetails.selectedOffers[0].apr.toFixed(3) + '%';
  }

  public get originationFee(): number {
    return this.loanDetails.selectedOffers[0].originationFee;
  }

  public get amountFinanced(): number {
    return this.loanDetails.selectedOffers[0].amountFinanced;
  }

  public isMitekEnabled: boolean = false;

  constructor(
    private api: LoanApplicationOffersService,
    private applicationProgress: PLApplicationProgressService,
    private featureToggleService: FeatureToggleService,
    private sessionService: SessionService) { }

  ngOnInit() {
    this.isLoading = true;
    this.featureToggleService.isMitekEnabled.subscribe(v => this.isMitekEnabled = v);

    // for LendingTree entry
    this.api.getSelectedLoanApplicationOffer().subscribe(
      response => {
        response.selectedOffers[0].apr = Utils.formatFloat(response.selectedOffers[0].apr * 100, 3);
        response.selectedOffers[0].interestRate = Utils.formatFloat(response.selectedOffers[0].interestRate * 100, 2);
        this.loanDetails = response;
        this.sessionService.personalLoanSession.data.loanNumber = this.loanDetails.loanApplication.loanNumber;

        // The showUploadDocumentsHelpText variable will default to false unless otherwise specified.
        // It will be true for self service and for that we will skip this evaluation.
        if (!this.showUploadDocumentsHelpText) {
          const isFinishingApp = this.applicationProgress.activeSubStep === 'finishApplication';
          const hasLoanApp = !!this.loanDetails.loanApplication;
          const hasUploads = this.loanApplicationState === LoanApplicationStateEnum.Reviewing;
          this.showUploadDocumentsHelpText = isFinishingApp && hasLoanApp && hasUploads;
        }
      },
      err => {
        console.error(err);
        this.loanDetails = null;
      },
      () => { this.isLoading = false; }
      );
  }
}
